// Stateless timeline and selected-ride details; callbacks preserve original ride identity.
import React from 'react';
import { zoneNameFor } from '../../i18nFormat';
import { getStyle } from '../../utils/utils';
import { fleetLocationStyles as styles } from './fleetLocationStyles';
import { boundaryLabel } from './historyData';
import { RIDE_COLOURS, rideKey, rideStats, rideElapsedSeconds,
  dayTotals, formatDistance, formatDuration } from './historyPresentation';

const EMPTY_HISTORY_MESSAGES = {
  moved: 'historyObservedNoRides',
  still: 'historyNoRides',
  unknown: 'historyInsufficientEvidence',
};

function VehicleHistoryTimeline({ history, t, language, screenSize, onSelectRide, clampedAt }) {
  const { status, rides, emptyState, truncated,
    coveredFrom, message, selected, selectedNames } = history;
  // The zone the whole table is read in, taken from the first timestamp the day has. A row
  // whose own zone differs -- the two nights a year the clocks move -- says so itself.
  function zoneOfDay() {
    for (let i = 0; i < rides.length; i += 1) {
      const ends = [rides[i].start, rides[i].end];
      for (let j = 0; j < ends.length; j += 1) {
        if (ends[j] && ends[j].ts) {
          return zoneNameFor(ends[j].ts, language);
        }
      }
    }
    return null;
  }

  // The cell prints the zone ONLY when it is not the one named above the table.
  function renderTime(boundary, fallbackKey, dayZone) {
    if (!boundary || !boundary.ts) {
      return t(fallbackKey);
    }
    const zone = zoneNameFor(boundary.ts, language);
    return (
      <span>
        {t('historyRideTime', { time: boundary.ts })}
        {zone && dayZone && zone !== dayZone && (
          <span style={{ marginLeft: 4, opacity: 0.7 }}>{zone}</span>
        )}
      </span>
    );
  }

  function renderRow(ride, index, screenSize, dayZone) {
    const isSelected = selected === ride;
    const swatch = RIDE_COLOURS[index % RIDE_COLOURS.length];
    const average = rideStats(ride).avgSpeedKmh;
    return (
      <tr
        key={rideKey(ride, index)}
        data-ride={index}
        data-span-kind='ride'
        onClick={() => onSelectRide(ride)}
        style={{
          cursor: 'pointer',
          background: isSelected ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
        }}
      >
        <td style={getStyle(screenSize, styles, 'tableCell')}>
          <button type='button' aria-pressed={isSelected}
            onClick={(event) => {
              event.stopPropagation();
              onSelectRide(ride);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                onSelectRide(ride);
              }
            }}
            style={{ font: 'inherit', color: 'inherit', background: 'transparent',
              border: 0, padding: 0, textAlign: 'inherit', cursor: 'pointer' }}>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 5,
                marginRight: 8,
                background: swatch,
              }}
            />
            {renderTime(ride.start, 'historyRideOpenStart', dayZone)}
          </button>
        </td>
        <td style={getStyle(screenSize, styles, 'tableCell')}>
          {renderTime(ride.end, 'historyRideRunning', dayZone)}
        </td>
        <td style={getStyle(screenSize, styles, 'tableCell')}>{formatDistance(ride.distance_km)}</td>
        <td style={getStyle(screenSize, styles, 'tableCell')}>
          {formatDuration(rideElapsedSeconds(ride))}
        </td>
        <td style={getStyle(screenSize, styles, 'tableCell')}>
          {average === null ? '—' : average.toFixed(1)}
        </td>
      </tr>
    );
  }

  const dayZone = zoneOfDay();
  const totals = dayTotals(rides);
  const stats = selected ? rideStats(selected) : null;
  return (
    <div style={{ flex: '1 1 380px', minWidth: 0 }}>
        {status === 'loading' && (
          <p style={getStyle(screenSize, styles, 'summary')}>{t('loading')}</p>
        )}
        {status === 'error' && <p style={getStyle(screenSize, styles, 'error')}>{message}</p>}
        {/* A TRUNCATED DAY SAYS WHERE IT STARTS. "Part of the day only" leaves the reader
            guessing which part; the endpoint already reports the first instant it covered. */}
        {status === 'ok' && truncated && (
          <p style={getStyle(screenSize, styles, 'staleBanner')}>
            {t('historyTruncated')}
            {coveredFrom && ' ' + t('historyCoveredFrom', { time: coveredFrom })}
          </p>
        )}
        {/* THE DAY THE CLOCKS GAVE BACK AN HOUR. The endpoint takes 24 hours and this day
            has 25, so one hour of one night is not in anything below -- including the
            emptiness of a day with no rides, which is why this sits outside that branch.
            Ungated by `status` on purpose: the missing hour is a fact about the calendar
            and the window we asked for, true before the answer arrives and true if it
            never does. */}
        {clampedAt && (
          <p style={getStyle(screenSize, styles, 'summary')}>
            {t('historyDayClamped')}
            {' ' + clampedAt}
          </p>
        )}
        {status === 'ok' && rides.length === 0 && (
          <p style={getStyle(screenSize, styles, 'summary')}>
            {t(EMPTY_HISTORY_MESSAGES[emptyState] || EMPTY_HISTORY_MESSAGES.unknown)}
          </p>
        )}
        {status === 'ok' && rides.length > 0 && (
          <div>
            <table style={getStyle(screenSize, styles, 'table')}>
              <caption style={{ ...getStyle(screenSize, styles, 'summary'), textAlign: 'left' }}>
                {t('historyTitle')}
                {dayZone && ' \u00B7 ' + t('historyZoneNote', { zone: dayZone })}
              </caption>
              <thead>
                <tr>
                  <th style={getStyle(screenSize, styles, 'tableHeader')}>
                    {t('historyColumnStart')}
                  </th>
                  <th style={getStyle(screenSize, styles, 'tableHeader')}>
                    {t('historyColumnEnd')}
                  </th>
                  <th style={getStyle(screenSize, styles, 'tableHeader')}>
                    {t('historyColumnDistance')}
                  </th>
                  <th style={getStyle(screenSize, styles, 'tableHeader')}>
                    {t('historyColumnDuration')}
                  </th>
                  <th style={getStyle(screenSize, styles, 'tableHeader')}>
                    {t('historyColumnAverageSpeed')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rides.map((ride, index) => renderRow(ride, index, screenSize, dayZone))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope='row' colSpan={2} style={getStyle(screenSize, styles, 'tableHeader')}>
                    {t('historyTotalsLabel')}
                  </th>
                  <td style={getStyle(screenSize, styles, 'tableHeader')}>{formatDistance(totals.km)}</td>
                  <td style={getStyle(screenSize, styles, 'tableHeader')}>{formatDuration(totals.seconds)}</td>
                  <td style={getStyle(screenSize, styles, 'tableHeader')}>
                    {totals.avgSpeedKmh === null ? '—' : totals.avgSpeedKmh.toFixed(1)}
                  </td>
                </tr>
              </tfoot>
            </table>
            {/* WHAT THE VEHICLE REPORTED ABOUT THE JOURNEY IN HAND. Six columns of odometer
                and speed would make the table unreadable for the nine rides nobody clicked;
                the selected row is the one whose detail was asked for. */}
            {selected && (
              <div role='region' aria-label={t('historyDetailsTitle')}
                style={{ ...getStyle(screenSize, styles, 'notice'), marginTop: 24 }}>
                <h4 style={{ margin: '0 0 12px' }}>{t('historyDetailsTitle')}</h4>
                <div aria-live='polite' style={getStyle(screenSize, styles, 'summary')}>
                  {/* Both labelled slots always remain: a lone arrival name must never look
                      like a departure. Without a name, show its coordinate or an em dash. */}
                  <p>{t('historyPlaceStart')}: {boundaryLabel(
                    selected.start, selectedNames && selectedNames.start
                  )}</p>
                  <p>{t('historyPlaceEnd')}: {boundaryLabel(
                    selected.end, selectedNames && selectedNames.end
                  )}</p>
                </div>
                <p style={{ ...getStyle(screenSize, styles, 'summary'), marginTop: 12 }}>
                  {t('historyOdometer', {
                    start: formatDistance(stats.odometerStart),
                    end: formatDistance(stats.odometerEnd),
                  })}
                  {stats.avgSpeedKmh !== null
                    && ' \u00B7 ' + t('historySpeedAvg', { speed: stats.avgSpeedKmh.toFixed(1) })}
                  {stats.fastestSampleKmh !== null
                    && ' \u00B7 ' + t('historySpeedFastestSample', {
                      speed: Math.round(stats.fastestSampleKmh),
                    })}
                </p>
              </div>
            )}
            <details style={{ ...getStyle(screenSize, styles, 'summary'), marginTop: 12, fontSize: 13 }}>
              <summary style={{ cursor: 'pointer' }}>{t('historyHelp')}</summary>
              <p>{t('historyTimestampNote')}</p>
              <p>{t('historyDirectionNote')}</p>
            </details>
          </div>
        )}
    </div>
  );
}

export default VehicleHistoryTimeline;
